import React, { useState } from "react";
import Tile from "../tile";

import dishDiaryWebp from "../../img/dishdiary.webp";
import serendipityWebp from "../../img/serendipity.webp";
import project2 from "../../img/doc.png";
import project3 from "../../img/project_3.png";
import project4 from "../../img/project_4.png";
import project5 from "../../img/carpool.png";
import belLogo from "../../img/bel.jpg";

import "../styles/pages/portfolio.scss";

const ALL_PROJECTS = [
  {
    id: "dishdiary",
    title: "DishDiary",
    badge: "Featured · Android + Web",
    featured: true,
    brief: "AI recipe & meal-planning platform with Flutter Android app, Next.js web app, Express/Prisma/PostgreSQL API, and Auth0 login.",
    src: dishDiaryWebp,
    alt: "DishDiary AI Recipe and Meal Planning App",
    tech: ["Flutter", "Next.js", "TypeScript", "Node.js", "Express", "Prisma", "PostgreSQL", "Docker", "Nginx", "Auth0", "Firebase", "Groq/OpenRouter"],
    link: "https://dishdiaryy.vercel.app",
    github: "https://github.com/PratyushKumarRai/DishDiary",
    categories: ["Full-Stack", "AI/ML", "Mobile"],
    details: [
      "AI backend using Groq with OpenRouter fallback, async jobs, per-user quotas, token throttling and food-only guardrails.",
      "API deployed on a Hetzner VPS with Docker, Nginx and HTTPS; JWT verification, rate limiting, SWR/ETag caching.",
      "Features YouTube recipe import, AI meal plans, guided cooking with TTS, private circles, and community feed.",
      "Synergized architecture: Flutter mobile client with Isar offline storage + Next.js web app with Auth0 authentication."
    ]
  },
  {
    id: "serendipity",
    title: "Serendipity",
    badge: "Featured · Team Project",
    featured: true,
    brief: "Candid photo discovery and micro-tipping platform. Revamped serendi.pt with a modern Nuxt 3 UI alongside full-stack Go development.",
    src: serendipityWebp,
    alt: "Serendipity Candid Photo Discovery Platform",
    tech: ["Go", "Nuxt 3", "Vue.js", "PostgreSQL", "Docker", "Auth0 (M2M)", "AWS S3", "Tailwind CSS", "Nginx", "Linux"],
    link: "https://serendi.pt",
    categories: ["Full-Stack"],
    details: [
      "Revamped serendi.pt with a modern Nuxt 3 (Vue 3, Pinia) UI alongside full-stack Go development.",
      "3-strike automated moderation via the Auth0 Management API (OAuth2 M2M) with transactional email alerts.",
      "PostgreSQL migrations with ON DELETE CASCADE across 8 tables, plus an admin moderation console.",
      "Deployed to a Hetzner VPS using Docker Compose and Nginx."
    ]
  },
  {
    id: "derm-detection",
    title: "Dermatological Disease Detection",
    badge: "Research Publication",
    featured: false,
    brief: "Real-time deep learning framework using YOLOv5 and Python for dermatological disease detection and classification.",
    src: project4,
    alt: "Skin Disease Detection using YOLOv5",
    tech: ["Python", "YOLOv5", "PyTorch", "OpenCV", "NumPy", "Google Colab", "Git"],
    link: "https://www.sciencedirect.com/science/article/pii/S1877050925010889",
    publication: "https://www.sciencedirect.com/science/article/pii/S1877050925010889",
    categories: ["AI/ML"],
    details: [
      "Built a real-time computer vision system using YOLOv5 and Python, achieving 0.9 mAP@0.5 across 7 classes.",
      "Implemented temporal analysis algorithms for early acne and lesion diagnosis.",
      "Published and presented peer-reviewed research at FTNCT 2024 (Elsevier / ScienceDirect)."
    ]
  },
  {
    id: "doctor-app",
    title: "Doctor App with Chatbot",
    badge: "AI Healthcare",
    featured: false,
    brief: "AI-powered healthcare mobile app in Flutter with a Transformer-based chatbot fine-tuned on Mistral-7B.",
    src: project2,
    alt: "Doctor App with Chatbot",
    tech: ["Flutter", "Dart", "Python", "Mistral-7B", "QLoRA", "Transformers", "PEFT", "NLP"],
    github: "https://github.com/PratyushKumarRai/Doctor-App-with-ChatBot",
    categories: ["AI/ML", "Mobile"],
    details: [
      "Developed a cross-platform AI healthcare mobile application using Flutter (Android & iOS).",
      "Fine-tuned Mistral-7B using QLoRA (4-bit) over 3 epochs, achieving 6.5 cross-entropy loss for medical Q&A.",
      "Engineered patient consultation pipelines and secure conversational interfaces."
    ]
  },
  {
    id: "smart-security",
    title: "Smart Security System",
    badge: "IoT & Systems",
    featured: false,
    brief: "Full-stack IoT human detection and surveillance system using ESP32-CAM with Wi-Fi and Bluetooth.",
    src: project3,
    alt: "IoT Smart Security System",
    tech: ["ESP32-CAM", "C/C++", "Firebase", "Ultrasonic Sensor", "IoT"],
    github: "https://github.com/PratyushKumarRai/IOT-Security-System",
    categories: ["Systems"],
    details: [
      "Spearheaded development of a full-stack IoT system using ESP32-CAM (OV2640) with Wi-Fi and Bluetooth.",
      "Integrated ultrasonic sensors for real-time human detection within 30 cm and automated image capture.",
      "Integrated Firebase as the backend to enable scalable remote image handling and surveillance."
    ]
  },
  {
    id: "carpool",
    title: "CarPooling App",
    badge: "Mobile App",
    featured: false,
    brief: "Flutter-based ride-sharing application with real-time matching and Appwrite cloud backend.",
    src: project5,
    alt: "CarPooling App",
    tech: ["Flutter", "Appwrite", "Dart"],
    github: "https://github.com/PratyushKumarRai/CarPooling-App",
    categories: ["Full-Stack", "Mobile"],
    details: [
      "Developed a Flutter-based carpooling mobile application for ride creation and passenger matching.",
      "Implemented comprehensive user profile workflows and real-time ride tracking.",
      "Leveraged Appwrite for user authentication, relational database, and storage."
    ]
  }
];

const CATEGORIES = ["All", "Full-Stack", "AI/ML", "Mobile", "Systems"];

function Portfolio() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredProjects = activeCategory === "All"
    ? ALL_PROJECTS
    : ALL_PROJECTS.filter((p) => p.categories.includes(activeCategory));

  return (
    <section id="portfolio" className="anchor">
      {/* ================= PROJECTS ================= */}
      <header className="section-header reveal-on-scroll">
        <span className="section-eyebrow">PORTFOLIO</span>
        <h2>FEATURED PROJECTS</h2>
        <p className="section-subtitle">
          End-to-end full stack web platforms, distributed backends, mobile apps, and machine learning models.
        </p>
      </header>

      {/* FILTER BAR */}
      <div className="filter-bar reveal-on-scroll" role="tablist" aria-label="Project Categories">
        {CATEGORIES.map((cat) => {
          const count = cat === "All" 
            ? ALL_PROJECTS.length 
            : ALL_PROJECTS.filter((p) => p.categories.includes(cat)).length;

          return (
            <button
              key={cat}
              role="tab"
              aria-selected={activeCategory === cat}
              className={`filter-btn ${activeCategory === cat ? "active" : ""}`}
              onClick={() => setActiveCategory(cat)}
            >
              <span>{cat}</span>
              <span className="filter-count">{count}</span>
            </button>
          );
        })}
      </div>

      {/* PROJECTS GRID */}
      <div className="tile-wrap">
        {filteredProjects.map((project) => (
          <Tile
            key={project.id}
            title={project.title}
            badge={project.badge}
            featured={project.featured}
            brief={project.brief}
            src={project.src}
            alt={project.alt}
            tech={project.tech}
            link={project.link}
            github={project.github}
            publication={project.publication}
            details={project.details}
          />
        ))}
      </div>

      {/* ================= EXPERIENCE ================= */}
      <header className="section-header experience-header">
        <span className="section-eyebrow">CAREER</span>
        <h2>PROFESSIONAL EXPERIENCE</h2>
        <p className="section-subtitle">
          Mission-critical defense software, real-time data parsing, and enterprise visualization.
        </p>
      </header>

      <div className="timeline-container">
        <div className="timeline-item">
          <div className="timeline-marker" aria-hidden="true"></div>
          <div className="experience-card vertical">
            <div className="experience-top-row">
              <div className="experience-logo">
                <img src={belLogo} alt="Bharat Electronics Limited (BEL)" loading="lazy" />
              </div>
              <div className="experience-meta">
                <h3 className="experience-role">
                  Software Developer (Apprentice)
                </h3>
                <span className="experience-company">
                  Bharat Electronics Limited (BEL), Ministry of Defence
                </span>
                <span className="experience-location">Bangalore, India</span>
                <span className="experience-duration">
                  <i className="fa fa-calendar-o" aria-hidden="true"></i> Jan 2025 – Jan 2026
                </span>
              </div>
            </div>

            <ul className="experience-points">
              <li>
                Modernized legacy defense software by reverse-engineering and re-architecting the codebase into a high-performance <strong>C++/Qt</strong> application with real-time TCP data parsing, enabling accurate sonar frequency analysis for mission-critical simulations.
              </li>
              <li>
                Built a custom news aggregation dashboard and office utility websites using <strong>React.js</strong>, reducing administrative overhead for internal teams.
              </li>
              <li>
                Designed intuitive <strong>C++/Qt GUIs</strong> that turned complex system data into clear visualizations for defense applications.
              </li>
            </ul>

            <div className="experience-tags">
              <span>C++</span>
              <span>Qt</span>
              <span>React.js</span>
              <span>TCP/IP</span>
              <span>MySQL</span>
              <span>XAMPP</span>
              <span>Docker</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Portfolio;